export function greetingForHour(hour: number): string {
  if (!Number.isFinite(hour)) return "你好";
  if (hour < 6) return "夜深了";
  if (hour < 11) return "早上好";
  if (hour < 14) return "中午好";
  if (hour < 18) return "下午好";
  return "晚上好";
}

export type ContinuationKind =
  "ai" | "approval" | "conversation" | "document" | "meeting";

export interface ContinuationItem {
  id: string;
  kind: ContinuationKind;
  meta: string;
  path: string;
  status: string;
  title: string;
  updatedAt: string;
}

export interface HomeDocumentSummary {
  id: string;
  title: string;
  updatedAt: string;
}

export interface HomeConversationSummary {
  id: string;
  lastMessageAt?: string;
  lastMessagePreview?: string;
  name?: string;
  peerDisplayName?: string;
  updatedAt: string;
}

export interface HomeApprovalSummary {
  createdAt: string;
  id: string;
  reason?: string;
  requester?: { displayName?: string };
  title: string;
  updatedAt: string;
}

export interface HomeMeetingSummary {
  createdAt: string;
  id: string;
  maxParticipants: number;
  status: "active" | "ended" | "scheduled";
  title: string;
  updatedAt: string;
}

export interface HomeAIConversationSummary {
  id: string;
  title: string;
  updatedAt: string;
}

export interface HomeScheduledTaskSummary {
  availableAt: string;
  id: string;
  messages?: Array<{ content: string; role: "assistant" | "user" }>;
  status:
    | "queued"
    | "running"
    | "waiting_for_confirmation"
    | "sleeping"
    | "cancel_requested"
    | "succeeded"
    | "failed"
    | "timed_out"
    | "cancelled";
  taskMode: "immediate" | "long_running" | "scheduled";
}

export interface UpcomingTaskItem {
  availableAt: string;
  id: string;
  title: string;
}

export function buildUpcomingTasks(
  runs: readonly HomeScheduledTaskSummary[],
  limit = 3,
): UpcomingTaskItem[] {
  const terminalStatuses = new Set([
    "cancelled",
    "failed",
    "succeeded",
    "timed_out",
  ]);

  return runs
    .filter(
      (run) =>
        run.taskMode === "scheduled" &&
        !terminalStatuses.has(run.status) &&
        Number.isFinite(Date.parse(run.availableAt)),
    )
    .sort(
      (left, right) =>
        Date.parse(left.availableAt) - Date.parse(right.availableAt),
    )
    .slice(0, Math.max(0, limit))
    .map((run) => ({
      availableAt: run.availableAt,
      id: run.id,
      title:
        [...(run.messages ?? [])].reverse().find(({ role }) => role === "user")
          ?.content ?? "未命名任务",
    }));
}

export function formatUpcomingTaskTime(value: string): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return "时间待定";
  return new Intl.DateTimeFormat("zh-CN", {
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
  }).format(new Date(timestamp));
}

export function buildContinuationItems(
  input: {
    ai?: readonly HomeAIConversationSummary[];
    approvals?: readonly HomeApprovalSummary[];
    conversations?: readonly HomeConversationSummary[];
    documents?: readonly HomeDocumentSummary[];
    meetings?: readonly HomeMeetingSummary[];
  },
  limit = 5,
): ContinuationItem[] {
  const candidates: ContinuationItem[] = [];
  const meeting = newest(input.meetings);
  const approval = newest(input.approvals);
  const conversation = newest(input.conversations);
  const document = newest(input.documents);
  const ai = newest(input.ai);

  if (meeting) {
    candidates.push({
      id: meeting.id,
      kind: "meeting",
      meta: `进行中 · 最多 ${meeting.maxParticipants} 人`,
      path: `/apps/meeting?room=${encodeURIComponent(meeting.id)}`,
      status: "加入会议",
      title: meeting.title || "未命名会议",
      updatedAt: meeting.updatedAt || meeting.createdAt,
    });
  }
  if (approval) {
    candidates.push({
      id: approval.id,
      kind: "approval",
      meta: [approval.requester?.displayName, approval.reason]
        .filter(Boolean)
        .join(" · "),
      path: `/approvals?approval=${encodeURIComponent(approval.id)}`,
      status: "待你处理",
      title: approval.title || "未命名审批",
      // /approvals/mine is ordered by createdAt, so keep the cross-domain
      // timestamp aligned with the item the source API defines as newest.
      updatedAt: approval.createdAt,
    });
  }
  if (conversation) {
    candidates.push({
      id: conversation.id,
      kind: "conversation",
      meta: conversation.lastMessagePreview || "继续最近的会话",
      path: `/apps/im?conversation=${encodeURIComponent(conversation.id)}`,
      status: "继续沟通",
      title: conversation.name || conversation.peerDisplayName || "会话",
      // /im/conversation/list is ordered by conversation.updatedAt. A title or
      // membership change may be newer than the last message and must not make
      // Home reorder a one-item source page using a different clock.
      updatedAt: conversation.updatedAt,
    });
  }
  if (document) {
    candidates.push({
      id: document.id,
      kind: "document",
      meta: "继续最近编辑的文档",
      path: `/apps/drive/resource/docs_document/${encodeURIComponent(document.id)}`,
      status: "继续编辑",
      title: document.title || "无标题文档",
      updatedAt: document.updatedAt,
    });
  }
  if (ai) {
    candidates.push({
      id: ai.id,
      kind: "ai",
      meta: "继续最近的 AI 对话",
      path: `/apps/ai?conversation=${encodeURIComponent(ai.id)}`,
      status: "继续对话",
      title: ai.title || "新对话",
      updatedAt: ai.updatedAt,
    });
  }

  return candidates
    .sort(
      (left, right) =>
        safeTime(Date.parse(right.updatedAt)) -
        safeTime(Date.parse(left.updatedAt)),
    )
    .slice(0, Math.max(0, limit));
}

export function formatContinuationTime(
  value: string,
  now = Date.now(),
): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return "时间未知";
  const elapsedMinutes = Math.max(0, Math.floor((now - timestamp) / 60_000));
  if (elapsedMinutes < 1) return "刚刚";
  if (elapsedMinutes < 60) return `${elapsedMinutes} 分钟前`;
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) return `${elapsedHours} 小时前`;
  const elapsedDays = Math.floor(elapsedHours / 24);
  if (elapsedDays < 7) return `${elapsedDays} 天前`;
  return new Intl.DateTimeFormat("zh-CN", {
    day: "numeric",
    month: "numeric",
  }).format(new Date(timestamp));
}

function newest<T extends { updatedAt: string }>(
  items: readonly T[] | undefined,
  timestamp: (item: T) => number = (item) => Date.parse(item.updatedAt),
): T | undefined {
  return items
    ?.slice()
    .sort(
      (left, right) => safeTime(timestamp(right)) - safeTime(timestamp(left)),
    )[0];
}

function safeTime(value: number): number {
  return Number.isFinite(value) ? value : 0;
}
